import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const FIELDS = [
  { key: "parent_name", label: "Parent Name", type: "text", placeholder: "Alex Rivera", testId: "contact-parent-name-input" },
  { key: "kids_names", label: "Your Ashley Falls Eagle Kid(s)", type: "text", placeholder: "Jamie Rivera & Sam Rivera", hint: "First and last name, please!", testId: "contact-kids-names-input" },
  { key: "email", label: "Email", type: "email", placeholder: "you@email.com", testId: "contact-email-input" },
  { key: "phone", label: "Phone Number", type: "tel", placeholder: "(858) 555-0123", testId: "contact-phone-input" },
];

export const ContactForm = ({ onSubmit, submitting }) => {
  const [values, setValues] = useState({ parent_name: "", kids_names: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    FIELDS.forEach(({ key, label }) => {
      if (!values[key].trim()) errs[key] = `${label} is required`;
    });
    if (values.kids_names.trim() && values.kids_names.trim().split(/\s+/).length < 2)
      errs.kids_names = "Please include first and last name";
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errs.email = "Please enter a valid email";
    setErrors(errs);
    if (Object.keys(errs).length === 0) onSubmit(values);
  };

  return (
    <div data-testid="survey-card" className="flex-1 flex flex-col justify-center px-6 py-10">
      <motion.h2
        className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
      >
        You made it!{" "}
        <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">🎉</span>
      </motion.h2>
      <motion.p
        className="mt-3 text-base sm:text-lg text-slate-600"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        Enter your details below to secure your{" "}
        <span className="font-bold text-pink-600">50 plushie entries</span>.
      </motion.p>

      <form data-testid="contact-form" onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5" noValidate>
        {FIELDS.map(({ key, label, type, placeholder, hint, testId }, i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.07 }}
          >
            <label htmlFor={key} className="font-display block text-xs font-extrabold uppercase tracking-[0.15em] text-slate-500">
              {label}
            </label>
            {hint && <p className="mt-1 text-xs font-bold text-cyan-600">{hint}</p>}
            <input
              id={key}
              data-testid={testId}
              type={type}
              placeholder={placeholder}
              value={values[key]}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              className={`mt-2 w-full rounded-xl border-2 bg-white px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-300 outline-none transition-colors focus:border-cyan-400 focus:shadow-[0_0_14px_rgba(6,182,212,0.3)] ${
                errors[key] ? "border-pink-500 shadow-[0_0_12px_rgba(236,72,153,0.25)]" : "border-slate-200"
              }`}
              style={{ transitionProperty: "border-color, box-shadow" }}
            />
            {errors[key] && (
              <p data-testid={`${testId}-error`} className="mt-1.5 text-xs font-bold text-pink-600">
                {errors[key]}
              </p>
            )}
          </motion.div>
        ))}

        <motion.button
          data-testid="contact-submit-button"
          type="submit"
          disabled={submitting}
          className="neon-pulse mt-2 w-full rounded-2xl bg-gradient-to-r from-pink-500 via-fuchsia-500 to-yellow-400 px-6 py-5 font-display text-lg font-black uppercase tracking-wide text-white transition-transform active:scale-[0.97] disabled:opacity-70"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          whileTap={{ scale: 0.97 }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={20} /> Securing entries...
            </span>
          ) : (
            "Submit & Get My Entries!"
          )}
        </motion.button>
      </form>
    </div>
  );
};
